import type { AuthProvider } from 'react-admin';

import { apiFetch, refreshAccessToken } from './api/client';
import { clearAccessToken, getAccessToken, setAccessToken } from './api/tokenStore';

export interface AuthUser {
  id: string;
  full_name: string;
  email: string;
  role: string;
  permissions: string[];
}

let currentUser: AuthUser | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

const REFRESH_INTERVAL_MS = 14 * 60 * 1000;

function startRefreshTimer(): void {
  stopRefreshTimer();
  refreshTimer = setInterval(async () => {
    if (getAccessToken()) {
      await refreshAccessToken();
    }
  }, REFRESH_INTERVAL_MS);
}

function stopRefreshTimer(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

async function revokeAdminSession(): Promise<void> {
  try {
    await apiFetch('/auth/logout?portal=admin', { method: 'POST', skipRefresh: true });
  } catch {
    // best-effort
  }
  clearAccessToken();
  currentUser = null;
}

async function fetchCurrentUser(): Promise<AuthUser> {
  const user = await apiFetch<AuthUser>('/auth/me');
  currentUser = user;
  return user;
}

async function rejectNonAdminUser(message: string): Promise<never> {
  stopRefreshTimer();
  await revokeAdminSession();
  throw new Error(message);
}

export const authProvider: AuthProvider = {
  login: async ({ username, password }) => {
    const response = await apiFetch<{ access_token: string }>('/auth/login', {
      method: 'POST',
      body: { email: username, password, portal: 'admin' },
      skipAuth: true,
      skipRefresh: true,
    });

    setAccessToken(response.access_token);
    try {
      const user = await fetchCurrentUser();
      if (user.role === 'business_owner') {
        await rejectNonAdminUser(
          'Business owner accounts must use the Owner Portal, not the Admin panel.',
        );
      }
    } catch (err) {
      await revokeAdminSession();
      throw err;
    }
    startRefreshTimer();
  },

  logout: async () => {
    stopRefreshTimer();
    await revokeAdminSession();
  },

  checkAuth: async () => {
    if (!getAccessToken()) {
      const token = await refreshAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
    }
    if (!currentUser) {
      const user = await fetchCurrentUser();
      if (user.role === 'business_owner') {
        await revokeAdminSession();
        throw new Error('Not authenticated');
      }
    }
    startRefreshTimer();
  },

  checkError: async (error) => {
    const status = (error as { status?: number })?.status;
    if (status === 401) {
      const token = await refreshAccessToken();
      if (!token) {
        await revokeAdminSession();
        stopRefreshTimer();
        throw error;
      }
      return Promise.resolve();
    }
    return Promise.resolve();
  },

  getIdentity: async () => {
    const user = currentUser ?? (await fetchCurrentUser());
    return {
      id: user.id,
      fullName: user.full_name,
      avatar: undefined,
    };
  },

  getPermissions: async () => {
    const user = currentUser ?? (await fetchCurrentUser());
    return user.permissions;
  },
};

export function getCurrentUser(): AuthUser | null {
  return currentUser;
}

export function hasPermission(permission: string): boolean {
  if (currentUser?.role === 'super_admin') return true;
  return currentUser?.permissions.includes(permission) ?? false;
}
